import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { IVbrickPlayerConfig } from '@vbrick/vbrick-player';

import { CacheFactory } from 'rev-shared/util/CacheFactory';
import { UserAccountService } from 'rev-shared/security/UserAccount.Service';

import { IMediaFeatures } from './IMediaFeatures';
import { mergeWithDefaultPlayerConfig } from './VbrickPlayerDefaultConfig';
import { lastValueFrom } from 'rev-shared/rxjs/lastValueFrom';

@Injectable({
	'providedIn': 'root'
})
export class MediaFeaturesService {
	private readonly mediaFeatureSettingsCache: CacheFactory<IMediaFeatures | Promise<IMediaFeatures>>;


	constructor(
		private readonly UserAccount: UserAccountService,
		private readonly http: HttpClient
	) {
		this.mediaFeatureSettingsCache = new CacheFactory<IMediaFeatures | Promise<IMediaFeatures>>(3);
	}

	public get accountFeatures(): IMediaFeatures {
		return this.getAccountFeatures(this.UserAccount.workingAccountId);
	}

	public get playerConfig(): IVbrickPlayerConfig {
		return mergeWithDefaultPlayerConfig(this.accountFeatures.playerSettings);
	}

	public getAccountFeatures(accountId: string): IMediaFeatures {
		const cachedFeatures = this.mediaFeatureSettingsCache.get(accountId) as any;

		if (!cachedFeatures || cachedFeatures.then) {
			throw new Error('Account mediaFeatures not loaded');
		}

		return cachedFeatures;
	}

	public clearCache(accountId: string): void {
		this.mediaFeatureSettingsCache.remove(accountId);
	}

	//If account is loaded, update the cache
	public updateAccountFeatures(accountId: string, features: Partial<IMediaFeatures>): void {
		const cachedFeatures = this.mediaFeatureSettingsCache.get(accountId) as any;
		if(!cachedFeatures) {
			console.error('Cannot update features');
			return;
		}

		if(cachedFeatures.then) {
			cachedFeatures.then(() => Object.assign(cachedFeatures, features))
				.catch(e => console.error('Error updating account features', e));
			return;
		}

		Object.assign(cachedFeatures, features);
	}

	public getFeatures(accountId?: string): Promise<IMediaFeatures> {
		accountId = accountId || this.UserAccount.workingAccountId;
		const cachedFeatures = this.mediaFeatureSettingsCache.get(accountId);
		if(cachedFeatures) {
			return Promise.resolve(cachedFeatures);
		}

		const result = this.fetchMediaFeatures(accountId)
			.then(features => {
				this.mediaFeatureSettingsCache.put(accountId, features);
				return features;
			})
			.catch (e => {
				this.mediaFeatureSettingsCache.remove(accountId);
				throw e;
			});

		this.mediaFeatureSettingsCache.put(accountId, result);
		return result;
	}

	private fetchMediaFeatures(accountId: string): Promise<IMediaFeatures> {
		return lastValueFrom(this.http.get<any>(`/media/accounts/${accountId}/features`))
			.then(result => {
				const features = result.features;
				delete result.features;
				return {
					...result,
					...features,
					recordingPolicy: result.webcastRecordingPolicy,
					mfaEnabled: result.enableMfaSetting
				};
			});
	}
}
